/**
 * Shared drilldown navigation helper.
 *
 * The backend field registry returns a `drilldown_url` template per field
 * (e.g. `/suppliers?q={value}`). When a chart segment or grid row is
 * clicked, we substitute `{value}` with the row's identifier and navigate.
 *
 * Returns `null` if no template is configured or the row has no usable key.
 */
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

export function useDrilldown(template?: string | null) {
  const navigate = useNavigate();

  return useCallback(
    (rawValue: unknown) => {
      if (!template) return;
      const value = typeof rawValue === 'number' || typeof rawValue === 'string'
        ? String(rawValue)
        : '';
      if (!value) return;
      const resolved = template.replace('{value}', encodeURIComponent(value));
      // Absolute URL — open in new tab. Relative — client-side navigate.
      if (/^https?:\/\//i.test(resolved)) {
        window.open(resolved, '_blank', 'noopener');
      } else {
        navigate(resolved);
      }
    },
    [template, navigate],
  );
}
